import { useState } from "react";
import { DataAttributionNote } from "@/components/DataAttributionNote";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Activity } from "lucide-react";
import { format } from "date-fns";
import { de, enGB, es, ru, uk } from "date-fns/locale";
import type { Locale } from "date-fns";
import { api } from "@/api/client";
import { Card, SectionLabel } from "@/components/ui/Card";
import { fmtDate } from "@/lib/dates";
import { fmtDuration, SPORT_LABELS } from "@/lib/tss";
import i18n from "@/i18n";

type Tab = "today" | "calendar" | "list";

const _DATE_LOCALES: Record<string, Locale> = { en: enGB, ru, uk, es, de };
function _dateLocale(): Locale {
  const lng = (i18n.language || "en").slice(0, 2).toLowerCase();
  return _DATE_LOCALES[lng] || enGB;
}

interface PlannedSlot {
  id: number;
  sport: string;
  type: string;
  title: string;
  duration_minutes: number;
  target_tss: number;
  is_compound: boolean;
}
interface CompletedSlot {
  id: number;
  sport: string;
  name: string;
  duration_min: number;
  tss: number | null;
  distance_km: number | null;
}
interface DayCell {
  date: string;
  status: "completed" | "planned" | "missed" | "rest";
  planned: PlannedSlot[];
  completed: CompletedSlot[];
}

const SPORT_ICON: Record<string, string> = {
  swim: "🏊", bike: "🚴", run: "🏃", strength: "💪",
  brick: "🔁", mobility: "🧘", rest: "—",
};

// SPORT_LABEL is now provided by tss.ts SPORT_LABELS (i18n proxy).

export default function Workouts() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("today");
  const tabLabels: Record<Tab, string> = {
    today: t("workouts.tab_today"),
    calendar: t("workouts.tab_calendar"),
    list: t("workouts.tab_list"),
  };
  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header>
        <SectionLabel>{t("workouts.title")}</SectionLabel>
        <h1 className="mt-1 font-serif text-[26px] font-medium leading-snug md:text-[30px]">
          {tabLabels[tab]}
        </h1>
      </header>

      <div className="flex gap-1 overflow-x-auto scrollbar-hide border-b border-cream-border">
        {(["today", "calendar", "list"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-[13px] transition-colors ${
              tab === k
                ? "border-amber font-medium text-navy-deep"
                : "border-transparent text-taupe hover:text-navy-deep"
            }`}
          >
            {tabLabels[k]}
          </button>
        ))}
      </div>

      {tab === "today" ? <TodayView /> : tab === "calendar" ? <CalendarView /> : <ListView />}
      <DataAttributionNote />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Today
// ---------------------------------------------------------------------------

function TodayView() {
  const { t } = useTranslation();
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const todayIso = `${year}-${String(month).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const q = useQuery({
    queryKey: ["workouts-calendar", year, month],
    queryFn: async () => {
      const { data } = await api.get<{ days: DayCell[] }>(
        `/workouts/calendar?year=${year}&month=${month}`,
      );
      return data;
    },
  });
  if (q.isLoading) return <div className="py-6 text-center text-taupe">{t("nutrition.loading")}</div>;
  if (!q.data) return <div className="py-6 text-center text-coral">{t("common.error_short")}</div>;
  const cell = q.data.days.find((d) => d.date === todayIso);

  if (!cell || (cell.planned.length === 0 && cell.completed.length === 0)) {
    return (
      <Card>
        <div className="py-2 text-center">
          <Activity className="mx-auto h-6 w-6 text-amber-dark" strokeWidth={1.5} />
          <div className="mt-2 font-medium text-navy-deep">{t("workouts.rest_today_title")}</div>
          <div className="mt-1 text-[13px] text-taupe">
            {t("workouts.rest_today_desc")}
          </div>
        </div>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {cell.planned.map((p) => (
        <Card key={`p-${p.id}`}>
          <div className="flex items-baseline justify-between">
            <SectionLabel>{t("workouts.planned")}</SectionLabel>
            <span className="text-[10px] uppercase tracking-wider text-amber-dark">
              {SPORT_ICON[p.sport] || "•"} {SPORT_LABELS[p.sport] || p.sport}
            </span>
          </div>
          <h3 className="mt-1 font-serif text-[18px] font-medium text-navy-deep">{p.title}</h3>
          <div className="mt-1 text-[13px] text-taupe">
            {p.type} · {fmtDuration(p.duration_minutes)} · TSS {p.target_tss}
            {p.is_compound ? " · compound" : ""}
          </div>
          <Link
            to={`/workout/planned/${p.id}`}
            className="mt-2 inline-block text-[12px] font-medium text-amber-dark hover:underline"
          >
            {t("workouts.open_details")}
          </Link>
        </Card>
      ))}
      {cell.completed.map((c) => (
        <Card key={`c-${c.id}`}>
          <div className="flex items-baseline justify-between">
            <SectionLabel>{t("workouts.completed")}</SectionLabel>
            <span className="text-[10px] uppercase tracking-wider text-amber-dark">
              {SPORT_ICON[c.sport] || "•"} {SPORT_LABELS[c.sport] || c.sport}
            </span>
          </div>
          <h3 className="mt-1 font-serif text-[18px] font-medium text-navy-deep">
            {c.name || "Workout"}
          </h3>
          <div className="mt-1 text-[13px] text-taupe">
            {fmtDuration(c.duration_min)}
            {c.tss != null ? ` · TSS ${c.tss}` : ""}
            {c.distance_km != null ? ` · ${c.distance_km} ${t("distance.km")}` : ""}
          </div>
          <Link
            to={`/workout/${c.id}`}
            className="mt-2 inline-block text-[12px] font-medium text-amber-dark hover:underline"
          >
            {t("workouts.open_details")}
          </Link>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

function CalendarView() {
  const { t } = useTranslation();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const q = useQuery({
    queryKey: ["workouts-calendar", year, month],
    queryFn: async () => {
      const { data } = await api.get<{ days: DayCell[]; today: string }>(
        `/workouts/calendar?year=${year}&month=${month}`,
      );
      return data;
    },
  });
  const shift = (delta: number) => {
    let m = month + delta;
    let y = year;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    setYear(y); setMonth(m);
  };

  if (q.isLoading) return <div className="py-6 text-center text-taupe">{t("nutrition.loading")}</div>;
  if (!q.data) return <div className="py-6 text-center text-coral">{t("common.error_short")}</div>;

  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const monthLabel = format(new Date(year, month - 1, 1), "LLLL yyyy", { locale: _dateLocale() });
  // ISO weekday rotation Mon..Sun → date-fns short narrow names from a known
  // Monday (Jan 5 1970 was a Monday) so we get correct localized abbrev.
  const monRef = new Date(1970, 0, 5);
  const weekdayLabels = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(monRef);
    d.setDate(monRef.getDate() + i);
    return format(d, "EEEEEE", { locale: _dateLocale() });
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => shift(-1)} className="rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[13px] hover:bg-cream">
          <ChevronLeft className="inline h-3.5 w-3.5" /> {t("workouts.prev")}
        </button>
        <span className="text-[13px] text-navy-deep">{monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}</span>
        <button onClick={() => shift(1)} className="rounded-md border border-cream-border bg-cream-light px-3 py-1.5 text-[13px] hover:bg-cream">
          {t("workouts.next")} <ChevronRight className="inline h-3.5 w-3.5" />
        </button>
      </div>

      <Card>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-taupe">
          {weekdayLabels.map((d, i) => <div key={i} className="py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDow }).map((_, i) => (
            <div key={`l-${i}`} className="min-h-[88px]" aria-hidden="true" />
          ))}
          {q.data.days.map((d) => <CalendarDayCell key={d.date} day={d} todayIso={q.data.today} />)}
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-taupe">
          <Legend color="bg-amber/40" label={t("workouts.legend_completed")} />
          <Legend color="bg-cream-light" label={t("workouts.legend_planned")} />
          <Legend color="bg-coral/30" label={t("workouts.legend_missed")} />
          <Legend color="bg-cream/40" label={t("workouts.legend_rest")} />
        </div>
      </Card>
    </div>
  );
}

const SPORT_BADGE_BG: Record<string, string> = {
  swim: "bg-cyan-100 text-cyan-700",
  bike: "bg-blue-100 text-blue-700",
  run: "bg-orange-100 text-orange-700",
  strength: "bg-emerald-100 text-emerald-700",
  brick: "bg-purple-100 text-purple-700",
  mobility: "bg-amber/15 text-amber-dark",
  rest: "bg-cream text-taupe",
};

function CalendarDayCell({ day, todayIso }: { day: DayCell; todayIso: string }) {
  const dom = parseInt(day.date.slice(-2), 10);
  const isToday = day.date === todayIso;
  const tone = {
    completed: "border-amber/50 bg-amber/25",
    planned: "border-cream-border bg-cream-light",
    missed: "border-coral/40 bg-coral/10",
    rest: "border-dashed border-cream-border bg-cream/40",
  }[day.status];
  const todayCls = isToday
    ? " ring-2 ring-amber ring-offset-2 ring-offset-cream relative z-10"
    : "";
  const target = day.completed[0]
    ? `/workout/${day.completed[0].id}`
    : day.planned[0]
      ? `/workout/planned/${day.planned[0].id}`
      : null;

  const renderBadge = (sport: string, opacity: number, key: string) => (
    <span
      key={key}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-[20px] leading-none ${SPORT_BADGE_BG[sport] || "bg-cream text-taupe"}`}
      style={{ opacity }}
      title={SPORT_LABELS[sport] || sport}
    >
      {SPORT_ICON[sport] || "•"}
    </span>
  );

  const inner = (
    <div
      className={`flex min-h-[88px] flex-col justify-between rounded-md border p-1.5 transition-shadow hover:shadow-soft ${tone}${todayCls}`}
    >
      <span
        className={
          isToday
            ? "text-[13px] font-bold text-amber-dark"
            : "text-[12px] font-medium text-navy-deep"
        }
      >
        {dom}
      </span>
      <div className="flex flex-wrap items-end justify-end gap-1">
        {day.completed.slice(0, 2).map((c) => renderBadge(c.sport, 1, `c-${c.id}`))}
        {day.completed.length === 0 &&
          day.planned.slice(0, 2).map((p) => renderBadge(p.sport, 0.6, `p-${p.id}`))}
      </div>
    </div>
  );
  return target ? <Link to={target}>{inner}</Link> : inner;
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-3 w-3 rounded-sm ${color}`} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

interface ListItem {
  kind: "completed" | "planned" | "missed";
  id: number;
  date: string;
  sport: string;
  name: string;
  duration_min?: number;
  tss?: number | null;
  target_tss?: number;
  distance_km?: number | null;
  type?: string;
}
interface ListGroup {
  week: string;
  items: ListItem[];
  completed_count: number;
  planned_count: number;
  missed_count: number;
  total_tss: number;
}

function ListView() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<"all" | "completed" | "planned">("all");
  const [sportFilter, setSportFilter] = useState<string>("all");
  const statusLabel: Record<"all" | "completed" | "planned", string> = {
    all: t("workouts.filter_all"),
    completed: t("workouts.filter_completed"),
    planned: t("workouts.filter_planned"),
  };

  const q = useQuery({
    queryKey: ["workouts-list", statusFilter, sportFilter],
    queryFn: async () => {
      const { data } = await api.get<{ groups: ListGroup[] }>(
        `/workouts/list?days=60&status=${statusFilter}&sport=${sportFilter}`,
      );
      return data;
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {(["all", "completed", "planned"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setStatusFilter(k)}
            className={`rounded-full border px-3 py-1 text-[12px] ${
              statusFilter === k
                ? "border-amber bg-amber/15 font-medium text-navy-deep"
                : "border-cream-border text-taupe hover:bg-cream"
            }`}
          >
            {statusLabel[k]}
          </button>
        ))}
        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="ml-auto rounded-md border border-cream-border bg-cream-light px-2 py-1 text-[12px]"
        >
          <option value="all">{t("workouts.all_sports")}</option>
          <option value="swim">{t("sport.swim")}</option>
          <option value="bike">{t("sport.bike")}</option>
          <option value="run">{t("sport.run")}</option>
          <option value="strength">{t("sport.strength")}</option>
        </select>
      </div>

      {q.isLoading ? (
        <div className="py-6 text-center text-taupe">{t("nutrition.loading")}</div>
      ) : !q.data ? (
        <div className="py-6 text-center text-coral">{t("common.error_short")}</div>
      ) : q.data.groups.length === 0 ? (
        <Card>
          <p className="text-center text-[13px] text-taupe">{t("workouts.none_found")}</p>
        </Card>
      ) : (
        q.data.groups.map((g) => (
          <Card key={g.week}>
            <div className="mb-2 text-[11px] uppercase tracking-wider text-amber-dark">
              {g.week} · {g.completed_count} {t("workouts.week_summary_completed")} / {g.planned_count} {t("workouts.week_summary_plan")}
              {g.missed_count > 0 ? ` / ${g.missed_count} ${t("workouts.week_summary_missed")}` : ""}
              {" · TSS "}
              {g.total_tss}
            </div>
            <div className="space-y-1.5">
              {g.items.map((it) => <ListRow key={`${it.kind}-${it.id}`} item={it} />)}
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

function ListRow({ item }: { item: ListItem }) {
  const { t } = useTranslation();
  const target = item.kind === "completed" ? `/workout/${item.id}` : `/workout/planned/${item.id}`;
  const tone =
    item.kind === "completed"
      ? "border-amber/40 bg-amber/10"
      : item.kind === "missed"
        ? "border-coral/40 bg-coral/10"
        : "border-cream-border bg-cream-light";
  const badgeText =
    item.kind === "completed" ? "✓"
    : item.kind === "missed" ? t("workouts.row_missed_short")
    : t("workouts.row_planned_short");
  return (
    <Link
      to={target}
      className={`flex items-center justify-between rounded-md border px-3 py-2 text-[13px] hover:shadow-soft ${tone}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="metric-number text-[12px] text-taupe">{item.date.slice(5)}</span>
        <span className="text-[14px]">{SPORT_ICON[item.sport] || "•"}</span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-navy-deep">{item.name || SPORT_LABELS[item.sport] || item.sport}</div>
          <div className="text-[11px] text-taupe">
            {item.duration_min ? fmtDuration(item.duration_min) : ""}
            {item.tss != null ? ` · TSS ${item.tss}` : item.target_tss != null ? ` · ${t("workouts.tss_planned_short")} ${item.target_tss}` : ""}
            {item.distance_km != null ? ` · ${item.distance_km} ${t("distance.km")}` : ""}
          </div>
        </div>
      </div>
      <span className="ml-2 shrink-0 rounded-full bg-cream px-2 py-0.5 text-[10px] uppercase tracking-wider text-taupe">
        {badgeText}
      </span>
    </Link>
  );
}
